import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, Pencil, Trash2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { listCategories, listTasks, createTask, updateTask, deleteTask } from '../api';
import { useToast } from '../hooks/use-toast';

export default function ChecklistDetail() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const { toast } = useToast();
  const [category, setCategory] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    listCategories().then(cats => setCategory(cats.find(c => c.id === categoryId)));
    listTasks(categoryId).then(setTasks).catch(() => {});
  }, [categoryId]);

  const doneCount = tasks.filter(t => t.done).length;
  const percent = tasks.length ? Math.round((doneCount/tasks.length)*100) : 0;

  const toggle = async (t) => {
    setTasks(tasks.map(x => x.id === t.id ? {...x, done: !x.done} : x));
    try { await updateTask(t.id, { done: !t.done }); } catch (e) { toast({ title: 'Gagal update', variant: 'destructive' }); }
  };
  const remove = async (id) => {
    setTasks(tasks.filter(t => t.id !== id));
    try { await deleteTask(id); toast({ title: 'Tugas dihapus' }); } catch (e) {}
  };
  const addTask = async () => {
    if (!newTitle.trim()) return;
    try {
      const created = await createTask({ title: newTitle, categoryId, done: false });
      setTasks([...tasks, created]);
      setNewTitle(''); setShowAdd(false);
      toast({ title: 'Tugas ditambahkan' });
    } catch (e) { toast({ title: 'Gagal menambahkan', variant: 'destructive' }); }
  };
  const startEdit = (t) => { setEditingId(t.id); setEditValue(t.title); };
  const saveEdit = async () => {
    const id = editingId;
    setTasks(tasks.map(t => t.id === id ? {...t, title: editValue} : t));
    setEditingId(null);
    try { await updateTask(id, { title: editValue }); } catch (e) {}
  };

  return (
    <div className="px-5 pt-8 lg:pt-10">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate('/app/checklist')} className="w-11 h-11 rounded-full bg-white border border-cream-200 flex items-center justify-center hover:bg-cream-50">
          <ChevronLeft className="w-5 h-5 text-sage-600" />
        </button>
        <div>
          <h1 className="font-serif text-3xl text-sage-700">{category?.name || '...'}</h1>
          <p className="text-sage-500 text-sm">{doneCount} dari {tasks.length} tugas selesai</p>
        </div>
      </div>

      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sage-700 font-medium text-sm">{category?.name}</div>
          <div className="text-terracotta-500 font-serif text-lg">{percent}%</div>
        </div>
        <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
          <div className="h-full bg-terracotta-500 transition-all" style={{width: `${percent}%`}} />
        </div>
      </div>

      {showAdd ? (
        <div className="bg-white rounded-2xl p-4 border border-terracotta-200 mb-4 flex gap-2">
          <Input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} placeholder="Nama tugas baru" className="h-10 bg-cream-50 border-cream-200 rounded-xl" />
          <Button onClick={addTask} className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-xl h-10">Tambah</Button>
          <Button variant="ghost" onClick={() => { setShowAdd(false); setNewTitle(''); }} className="text-sage-500 h-10">Batal</Button>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="w-full bg-terracotta-100/50 hover:bg-terracotta-100 border border-terracotta-100 rounded-2xl py-4 text-terracotta-600 font-medium mb-4 flex items-center justify-center gap-2 transition-colors">
          <Plus className="w-5 h-5" /> Tambah tugas
        </button>
      )}

      <div className="space-y-3">
        {tasks.map(t => (
          <div key={t.id} className="bg-white rounded-2xl border border-cream-200 p-4 flex items-center gap-3">
            <button onClick={() => toggle(t)} className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${t.done ? 'bg-sage-600' : 'bg-cream-100 border border-cream-200'}`}>
              {t.done && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
            </button>
            {editingId === t.id ? (
              <Input autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => e.key === 'Enter' && saveEdit()} className="h-9 bg-cream-50" />
            ) : (
              <div className={`flex-1 text-sm ${t.done ? 'line-through text-sage-400' : 'text-sage-700'}`}>{t.title}</div>
            )}
            <button onClick={() => startEdit(t)} className="w-8 h-8 rounded-full bg-cream-100 hover:bg-cream-200 flex items-center justify-center">
              <Pencil className="w-3.5 h-3.5 text-sage-500" />
            </button>
            <button onClick={() => remove(t.id)} className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center">
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          </div>
        ))}
        {tasks.length === 0 && <div className="text-center py-12 text-sage-400 text-sm">Belum ada tugas. Yuk tambahkan!</div>}
      </div>
    </div>
  );
}
